import * as z from 'zod';
import { isoDateTimeSchema, machineKeySchema, strict, uuidSchema } from './common.js';
import { paginationQuerySchema } from './pagination.js';
/**
 * AD-21: who acted, and how it went.
 *
 * The audit trail is the security ledger of record: append-only by privilege, hash-chained, and
 * verified independently by sequence contiguity. Two of its columns are closed sets, and both are
 * declared here rather than only in the database, because a value the API can produce that the
 * column cannot store is a failure on the path that exists to record failures.
 *
 * **There are three kinds of actor and only two of them have an id.** A `system` action — a
 * scheduled job, a boot-time seed, a reconciliation — has no principal behind it, and inventing a
 * synthetic user row for one would put a login-capable account in the table for something that can
 * never log in. So `system` carries no id at all, and the schema below says so as a discriminated
 * union rather than as a comment.
 *
 * **A user id and an API-key id are not interchangeable.** They are drawn from different tables
 * with different lifecycles, and a single `actorId` column that sometimes means one and sometimes
 * the other cannot be a foreign key to either. The database carries them as two nullable
 * references with a CHECK, and this union is that same statement on the wire.
 */
/** The three principals. `system` is the absence of one, named rather than left as a null. */
export const AUDIT_ACTOR_TYPES = ['user', 'api_key', 'system'];
export const auditActorTypeSchema = z.enum(AUDIT_ACTOR_TYPES);
/**
 * How the action ended.
 *
 * `denied` is the member that earns the enum: a refused action is at least as interesting as a
 * successful one, because a run of them is what an attack looks like from inside the trail, and a
 * design that recorded only successes would make the most important rows the ones that were never
 * written.
 */
export const AUDIT_RESULTS = ['ok', 'denied', 'error'];
export const auditResultSchema = z.enum(AUDIT_RESULTS);
/** Who performed an action, in the one shape every layer agrees on. */
export const auditActorSchema = z.discriminatedUnion('type', [
    z.strictObject({ type: z.literal('user'), userId: z.uuid() }),
    z.strictObject({ type: z.literal('api_key'), apiKeyId: z.uuid() }),
    z.strictObject({ type: z.literal('system') }),
]);
/**
 * An action or a subject type, as a machine token.
 *
 * A dotted lowercase identifier rather than an enum of the whole catalogue, because the catalogue
 * lives in `packages/server/src/audit/service.ts` — where the emitter of each action is declared
 * beside it — and duplicating a hundred-odd literals into the browser bundle would create a second
 * list free to disagree with the first. The route validates the token's SHAPE here and looks it up
 * against the real catalogue on the server, so an unknown action is a refusal rather than a filter
 * that silently matches nothing.
 */
export const auditActionSchema = z
    .string()
    .min(3)
    .max(64)
    .regex(/^[a-z][\d_a-z]*\.[a-z][\d_a-z]*$/u, { error: 'errors.validation.invalid_format' });
/**
 * What an auditor filters the trail by. Every member is optional; none of them is a value.
 *
 * **`direction` is OMITTED, deliberately.** A trail has one natural order — newest first — and
 * every schema in this package is strict, so a caller that sends `direction` is refused as an
 * unknown key rather than served an ordering the keyset cursor was not built for. Offering the
 * choice would double the number of orderings the cursor over `(occurred_at, seq)` has to be
 * correct under, for a screen whose whole purpose is "what just happened".
 */
export const auditQuerySchema = paginationQuerySchema.omit({ direction: true }).extend({
    actorUserId: uuidSchema.optional(),
    actorApiKeyId: uuidSchema.optional(),
    action: auditActionSchema.optional(),
    subjectType: machineKeySchema.optional(),
    subjectId: uuidSchema.optional(),
    result: auditResultSchema.optional(),
    from: isoDateTimeSchema.optional(),
    to: isoDateTimeSchema.optional(),
});
/**
 * One row of the trail, decrypted.
 *
 * **`seq` is a string.** The column is a `bigint`, and JSON has one number type whose integers stop
 * being exact at 2^53; the column that exists to prove nothing was removed from the trail is the
 * last one to round.
 *
 * **`detail` is returned decrypted, and that is the point of having it.** It is sealed under the
 * audit subkey rather than under a person key precisely so that it survives an erasure and stays
 * readable while the record it describes is being investigated, and `audit/redact.ts` refuses at
 * write time to admit anything but branded ids, schema keys, closed-set tokens, counts and
 * booleans — so there is no attributable value in it to withhold. A row written with no detail
 * carries `null` rather than an empty object, because "nothing was recorded" and "an empty record
 * was recorded" are different facts about the trail.
 */
export const auditEntrySchema = strict({
    id: uuidSchema,
    seq: z.string().regex(/^\d+$/u),
    occurredAt: isoDateTimeSchema,
    actor: auditActorSchema,
    sessionId: z.string().min(1).max(200).nullable(),
    action: auditActionSchema,
    subjectType: machineKeySchema.nullable(),
    subjectId: uuidSchema.nullable(),
    method: machineKeySchema.nullable(),
    result: auditResultSchema,
    correlationId: uuidSchema.nullable(),
    detail: z.record(z.string(), z.unknown()).nullable(),
});
/** One break in the hash chain: which check failed, and on which row. */
export const auditChainBreakSchema = strict({
    reason: z.string().min(1).max(64),
    seq: z.string().regex(/^\d+$/u),
    id: uuidSchema,
    occurredAt: isoDateTimeSchema,
});
/** The first discontinuity in `seq`: the number expected, and the one found, or none at the end. */
export const auditSequenceGapSchema = strict({
    expectedSeq: z.string().regex(/^\d+$/u),
    foundSeq: z.string().regex(/^\d+$/u).nullable(),
    id: uuidSchema.nullable(),
    occurredAt: isoDateTimeSchema.nullable(),
});
/**
 * Section 1.12's Integrity panel: last verification, rows verified, sequence contiguity, first
 * break if any.
 *
 * **`lastRun` is the recorded history and `live` is this request.** A panel that only showed a live
 * verification would lose the memory that makes a TRUNCATION detectable at all: both of the
 * verifier's checks read only the rows that are still there, so rows deleted from the END of the
 * trail leave a chain that verifies and a sequence contiguous from 1 to a smaller number. The
 * remembered high-water mark is the only thing that sees that, and it lives in
 * `audit_verifications`, which is what `lastRun` and `highWaterSeq` report.
 */
export const auditChainStatusSchema = strict({
    ok: z.boolean(),
    rowsVerified: z.int().nonnegative(),
    firstSeq: z.string().regex(/^\d+$/u).nullable(),
    lastSeq: z.string().regex(/^\d+$/u).nullable(),
    /** `max(last_seq)` over every recorded run: the ratchet, never the newest run's own figure. */
    highWaterSeq: z.string().regex(/^\d+$/u).nullable(),
    chain: strict({ ok: z.boolean(), firstBreak: auditChainBreakSchema.nullable() }),
    sequence: strict({ ok: z.boolean(), firstGap: auditSequenceGapSchema.nullable() }),
    lastRun: strict({
        ranAt: isoDateTimeSchema,
        ok: z.boolean(),
        chainOk: z.boolean(),
        sequenceOk: z.boolean(),
        rowsVerified: z.int().nonnegative(),
        durationMs: z.int().nonnegative(),
        breakReason: z.string().min(1).max(64).nullable(),
    }).nullable(),
});
/**
 * One action the trail can record, said in a way a person can read.
 *
 * **Served rather than bundled, which is the whole point.** The comment on
 * {@link auditActionSchema} above argues, correctly, against copying a hundred-odd literals into the
 * browser: a second list is a list free to disagree with the first. But an administrator cannot be
 * asked to type `auth.login` either. So the catalogue travels over the wire, derived at runtime from
 * the server's own table, and the browser renders what it is handed. There is one list, and the
 * screen that reads it cannot drift from it.
 *
 * **Both languages travel.** The alternative is locale negotiation on a route that has never needed
 * any, and it would mean re-fetching the catalogue when somebody changes language rather than
 * re-rendering it from cache. Two short strings per action is the cheaper answer by a wide margin.
 *
 * `group` is the token's own prefix — `person`, `credential`, `presence`, `auth` — carried rather
 * than parsed, so the picker's headings are the server's answer and not a `split('.')` in a
 * component. `subject` is what the action acts on, which is the same closed set the trail's own
 * `subjectType` column carries. The group's own HEADING travels once in {@link
 * auditActionCatalogueSchema}'s `groups` rather than repeated on each of its members, which is both
 * smaller and the only shape in which a group with no members is still nameable.
 */
export const auditActionDescriptorSchema = strict({
    action: auditActionSchema,
    group: machineKeySchema,
    subject: machineKeySchema,
    labels: strict({ es: z.string().min(1).max(120), en: z.string().min(1).max(120) }),
});
/**
 * Every action the trail can actually contain.
 *
 * **Live entries only.** A `reserved` entry is a promise with a name attached and nothing emits it,
 * so offering one in a picker is offering a filter that can only ever answer "no rows" — which on
 * an audit trail reads as "this never happened", the most misleading answer it can give. It is a
 * list rather than a page because it is a fixed vocabulary of the installation, not data about it.
 */
export const auditActionCatalogueSchema = strict({
    /** The headings, in the order the picker offers them, which is the catalogue's own order. */
    groups: z.array(strict({
        group: machineKeySchema,
        labels: strict({ es: z.string().min(1).max(120), en: z.string().min(1).max(120) }),
    })),
    items: z.array(auditActionDescriptorSchema),
});
//# sourceMappingURL=audit.js.map